import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { Button } from '../components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../trpc';
import { Spinner } from '../components/ui/spinner';
import posthog from 'posthog-js';
import { analytics } from '../lib/analytics';

export const Route = createFileRoute('/congratulations')({
  component: Congratulations,
  beforeLoad: () => {
    // Check for valid Stripe success parameter first (don't check auth yet due to timing)
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 [DEV] Congratulations route beforeLoad check:', {
        currentUrl: window.location.href,
        urlParams: urlParams.toString(),
        subscriptionParam: subscriptionStatus,
        sessionValidated: sessionStorage.getItem('congratulations_validated'),
        clerkLoaded: !!window.Clerk,
        hasUser: !!window.Clerk?.user
      });
    }
    
    // Allow access if coming from Stripe success - we'll validate auth in the component
    const hasValidAccess = subscriptionStatus === 'success' || 
                          sessionStorage.getItem('congratulations_validated') === 'true';
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 [DEV] Access validation:', {
        subscriptionStatus,
        hasValidAccess,
        willRedirect: !hasValidAccess
      });
    }
    
    if (!hasValidAccess) {
      // Store error message and redirect to dashboard
      sessionStorage.setItem('access_error', 'Invalid access to congratulations page.');
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 [DEV] Invalid access, redirecting to dashboard');
      }
      return redirect({
        to: '/dashboard',
        replace: true,
      });
    }
    
    // Mark as validated for this session
    sessionStorage.setItem('congratulations_validated', 'true');
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 [DEV] Access granted, proceeding to congratulations page');
    }
  },
});

function Congratulations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.id;
  const [isValidating, setIsValidating] = useState(true);
  
  // Get user data to validate billing setup
  const { data: currentUser, isLoading: userLoading } = trpc.users.getCurrentUser.useQuery(undefined, {
    enabled: !!userId,
  });
  
  // Get subscription data to validate active billing
  const { data: subscription, isLoading: subscriptionLoading } = trpc.stripe.getActiveSubscription.useQuery(undefined, {
    enabled: !!userId,
  });

  // Server-side subscription validation for security
  const { data: subscriptionValidation, isLoading: validationLoading } = trpc.stripe.validateRecentSubscription.useQuery(undefined, {
    enabled: !!userId,
  });

  // subscription_started should only fire after the SERVER confirms billing
  // is valid. The query param is forgeable, so we wait for
  // subscriptionValidation.valid in the effect below. Ref dedupes the emit.
  const subscriptionStartedTrackedRef = useRef(false);

  // Authentication and billing validation in component (after Clerk loads)
  useEffect(() => {
    if (!userId) {
      // No user ID means not authenticated - redirect to home
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 [DEV] No user ID in component, redirecting to home');
      }
      navigate({ to: '/', replace: true });
      return;
    }
    
    if (!userLoading && !subscriptionLoading && !validationLoading && currentUser && subscriptionValidation) {
      // Use server-side validation for security
      const hasValidBilling = subscriptionValidation.valid;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 [DEV] Component billing validation:', {
          hasStripeCustomerId: !!currentUser.stripeCustomerId,
          hasSubscription: !!subscription,
          planType: currentUser.planType,
          serverValidation: subscriptionValidation,
          hasValidBilling
        });
      }
      
      if (!hasValidBilling) {
        // User hasn't actually completed billing - redirect to dashboard
        const errorMessage = subscriptionValidation.reason || 'Please complete your billing setup first.';
        sessionStorage.setItem('access_error', errorMessage);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 [DEV] Invalid billing, redirecting to dashboard. Reason:', subscriptionValidation.reason);
        }
        navigate({ to: '/dashboard', replace: true });
        return;
      }
      
      // Validation passed - clear billing deferred flag and proceed
      const deferredKey = `billing_setup_deferred_${userId}`;
      localStorage.removeItem(deferredKey);
      localStorage.removeItem('user_is_new_for_congratulations'); // Clean up new user flag
      sessionStorage.removeItem('congratulations_validated'); // Clean up session flag

      if (!subscriptionStartedTrackedRef.current) {
        analytics.track('subscription_started');
        subscriptionStartedTrackedRef.current = true;
      }

      // Track successful billing completion
      posthog.capture('billing_setup_completed', {
        user_id: userId,
        plan_type: currentUser?.planType,
        stripe_customer_id: currentUser?.stripeCustomerId,
        has_subscription: !!subscription,
        subscription_id: subscription?.id,
        subscription_status: subscription?.status
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 [DEV] All validations passed, showing congratulations page');
      }
      setIsValidating(false);
    }
  }, [userLoading, subscriptionLoading, validationLoading, currentUser, subscription, subscriptionValidation, userId, navigate]);

  // Navigate to dashboard
  const navigateToDashboard = () => {
    navigate({ to: '/dashboard' });
  };

  // Optional: Auto-redirect after 60 seconds if user doesn't click
  useEffect(() => {
    const timer = setTimeout(() => {
      navigateToDashboard();
    }, 60000); // 60 seconds

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleGetStarted = () => {
    navigateToDashboard();
  };

  // Show loading spinner while validating
  if (isValidating || userLoading || subscriptionLoading || validationLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100'>
        <div className='text-center'>
          <Spinner size='lg' />
          <p className='mt-4 text-gray-500'>Validating access...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100'>
      <div className='max-w-lg mx-auto text-center p-8 bg-white rounded-lg shadow-lg'>
        <div className='text-6xl mb-6'>🎉</div>
        
        <h1 className='text-3xl font-bold text-gray-900 mb-4'>
          {t('congratulations.title')}
        </h1>
        
        <p className='text-gray-600 mb-6 leading-relaxed'>
          {t('congratulations.description')}
        </p>
        
        <div className='mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200'>
          <p className='text-sm text-gray-700 mb-3'>
            {t('congratulations.supportInfo')}
          </p>
          
          <div className='space-y-2'>
            <a 
              href='/faq' 
              target='_blank' 
              rel='noopener noreferrer'
              className='inline-block text-blue-600 hover:text-blue-800 underline text-sm font-medium'
            >
              {t('congratulations.faqLink')}
            </a>
            
            <div className='text-sm space-y-1'>
              <div>
                <span className='font-medium text-gray-700'>{t('congratulations.supportEmails.technical')}</span>{' '}
                <a 
                  href='mailto:support@trackplicant.com' 
                  className='text-blue-600 hover:text-blue-800 underline'
                >
                  support@trackplicant.com
                </a>
              </div>
              <div>
                <span className='font-medium text-gray-700'>{t('congratulations.supportEmails.billing')}</span>{' '}
                <a 
                  href='mailto:billing@trackplicant.com' 
                  className='text-blue-600 hover:text-blue-800 underline'
                >
                  billing@trackplicant.com
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <div className='space-y-3'>
          <Button 
            onClick={handleGetStarted}
            className='w-full bg-blue-600 hover:bg-blue-700 text-white py-3'
          >
            {t('congratulations.getStarted')}
          </Button>
          
          <p className='text-xs text-gray-500'>
            {t('congratulations.autoRedirect')}
          </p>
        </div>
      </div>
    </div>
  );
}